import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import {
  ButtonComponent,
  ButtonIcon,
  ButtonShape,
  ButtonSize,
  ButtonType,
} from '@app/components/button';
import { ConfirmDialogComponent } from '@app/components/confirm-dialog';
import { InputComponent, InputSize } from '@app/components/input';
import { SelectDropdownComponent } from '@app/components/select-dropdown';
import { ColumnType, TableComponent, TableConfig } from '@app/components/table';
import { TextAlign } from '@app/components/text';
import { ToastService, ToastType } from '@app/components/toast';
import { ViewAlign } from '@app/components/view';
import { AdminTenantService } from '@app/services/admin-tenant/admin-tenant.service';
import { Logger } from '@app/services/logger/logger.service';
import { TranslatorService } from '@app/services/translator/translator.service';
import { Spacing } from '@app/shared/types';
import { TranslatePipe } from '@ngx-translate/core';
import {
  EnterpriseEntityConfigName,
  EnterpriseEntityConfigValidation,
} from '@skyfit/core-data-library';

export enum ConfigTypes {
  CONFIG = 'config',
  SECRET = 'secret',
}

export interface TenantConfigItem {
  value: string | null;
  encrypted: boolean;
}

@Component({
  selector: 'app-advanced',
  standalone: true,
  imports: [
    ButtonComponent,
    CommonModule,
    ConfirmDialogComponent,
    InputComponent,
    SelectDropdownComponent,
    TableComponent,
    TranslatePipe,
  ],
  templateUrl: './advanced.component.html',
  styleUrl: './advanced.component.css',
})
export class AdvancedComponent implements OnInit {
  private readonly adminTenantService = inject(AdminTenantService);
  private readonly toastService = inject(ToastService);
  private readonly translatorService = inject(TranslatorService);
  private readonly logger = inject(Logger);
  private readonly reservedConfigKeys = [
    EnterpriseEntityConfigName.flexClientId,
  ];
  private readonly _customConfigFormFields = [
    EnterpriseEntityConfigName.emailDomains,
    EnterpriseEntityConfigName.emailRegex,
    EnterpriseEntityConfigName.faqUrl,
    EnterpriseEntityConfigName.memberPaid,
    EnterpriseEntityConfigName.privacyUrl,
    EnterpriseEntityConfigName.provisioningType,
    EnterpriseEntityConfigName.slugs,
    EnterpriseEntityConfigName.tosUrl,
  ];
  readonly ButtonIcon = ButtonIcon;
  readonly ButtonShape = ButtonShape;
  readonly ButtonSize = ButtonSize;
  readonly ButtonType = ButtonType;
  readonly InputSize = InputSize;
  readonly Spacing = Spacing;
  readonly TextAlign = TextAlign;
  readonly ViewAlign = ViewAlign;
  readonly tableConfig: TableConfig = {
    type: 'common.advanced',
    columns: [
      { key: 'type', label: 'common.type', maxLength: 50 },
      { key: 'key', label: 'common.key', maxLength: 50 },
      { key: 'value', label: 'common.value' },
      { key: 'options', label: '', columnType: ColumnType.options },
    ],
  };

  config = signal<Record<string, TenantConfigItem> | null>(null);
  configTypes = signal([ConfigTypes.CONFIG, ConfigTypes.SECRET] as const);
  tableData = computed(() => {
    if (!this.config()) {
      return [];
    }
    return Object.entries(this.config())
      .filter(
        ([key]) =>
          !this._customConfigFormFields.includes(
            key as EnterpriseEntityConfigName
          )
      )
      .map(([name, configValue]) => ({
        key: name,
        value: configValue.value,
        type: configValue.encrypted ? ConfigTypes.SECRET : ConfigTypes.CONFIG,
        options: [
          { action: 'remove', label: 'common.remove' },
          { action: 'edit', label: 'common.edit' },
        ],
      }));
  });
  editMode = signal(false);
  hideOptionIndex = signal<number>(null);
  inputChanged = signal(false);
  keyInputPlaceholder = signal(
    this.translatorService.translate('tenantConfiguration.advanced.selectAKey')
  );
  newConfigKey = signal<string | null>(null);
  newConfigType = signal(ConfigTypes.CONFIG);
  newConfigValue = signal<string | null>('');
  removeConfirmOpen = signal(false);
  selectedOption = signal('');
  valueInputPlaceholder = signal(
    this.translatorService.translate('common.enterValue')
  );
  currentAvailableKeys = computed(() => {
    return this.newConfigType() === ConfigTypes.CONFIG
      ? this.availableConfigKeys()
      : this.availableSecretKeys();
  });
  availableConfigKeys = computed<string[]>(() => {
    if (!this.config()) {
      return [];
    }
    const fullOptions = Object.keys(EnterpriseEntityConfigName);
    const unusedOptions = Object.keys(EnterpriseEntityConfigName).filter(
      (key: EnterpriseEntityConfigName) => {
        return (
          !this.availableSecretKeys()?.includes(key) &&
          !Object.keys(this.config()).includes(key) &&
          !this._customConfigFormFields?.includes(key) &&
          !this.reservedConfigKeys?.includes(key)
        );
      }
    );
    // Use full options list in edit mode to ensure the selected value is included
    return this.editMode() ? fullOptions : unusedOptions;
  });
  availableSecretKeys = computed<string[]>(() => {
    if (!this.config()) {
      return [];
    }
    const fullOptions = Object.keys(EnterpriseEntityConfigValidation).filter(
      (key: EnterpriseEntityConfigName) => {
        return EnterpriseEntityConfigValidation[key]?.encrypted;
      }
    );
    const unusedOptions = fullOptions.filter(
      (key: EnterpriseEntityConfigName) => {
        return (
          EnterpriseEntityConfigValidation[key]?.encrypted &&
          !Object.keys(this.config()).includes(key)
        );
      }
    );
    // Use full options list in edit mode to ensure the selected value is included
    return this.editMode() ? fullOptions : unusedOptions;
  });
  canAdd = computed(() => {
    return (
      !!this.newConfigValue() &&
      !!this.newConfigKey() &&
      !!this.newConfigType() &&
      !!this.inputChanged()
    );
  });
  typeInputPlaceholder = computed(() => {
    return this.newConfigType()
      ? this.newConfigType()
      : this.translatorService.translate('common.selectType');
  });

  ngOnInit(): void {
    this.setCurrentConfigs();
  }

  setCurrentConfigs(): void {
    this.config.set(this.adminTenantService.getTenantConfig() ?? {});
  }

  updateNewConfigType(type: ConfigTypes): void {
    this.inputChanged.set(true);
    this.newConfigKey.set(null);
    this.newConfigType.set(type);
    this.newConfigValue.set('');
  }

  updateNewConfigValue(value: string): void {
    this.inputChanged.set(true);
    this.newConfigValue.set(value);
  }

  updateConfig(): void {
    const config = {
      [this.newConfigKey()]: {
        value: this.newConfigValue(),
        encrypted: this.newConfigType() === ConfigTypes.SECRET,
      },
    };
    this.adminTenantService.updateTenantConfig(config).subscribe({
      next: () => {
        this.toastService.add(
          this.translatorService.translate(
            'tenantConfiguration.general.configUpdated'
          ),
          ToastType.INFO
        );
        this.setCurrentConfigs();
        if (this.editMode()) {
          this.cancelEdit();
        }
      },
      error: (error) => {
        this.toastService.add(
          this.translatorService.translate(
            'tenantConfiguration.general.couldNotUpdateConfig'
          ),
          ToastType.ERROR
        );
        this.logger.error(error);
      },
    });
  }

  onOptionSelected(event: {
    action: string;
    key: string;
    value: string;
    index: number;
    type: ConfigTypes;
  }): void {
    this.selectedOption.set(event.key);
    if (event.action === 'remove') {
      this.openConfirmationDialog();
    } else if (event.action === 'edit') {
      this.editConfig(event);
    }
  }

  removeConfig(): void {
    const key = this.selectedOption();
    const { [key]: _, ...rest } = this.config();

    this.adminTenantService.removeTenantConfig(rest).subscribe({
      next: () => {
        this.closeConfirmationDialog();
        this.toastService.add(
          this.translatorService.translate(
            'tenantConfiguration.general.configRemoved'
          ),
          ToastType.INFO
        );
        this.setCurrentConfigs();
      },
      error: (error) => {
        this.closeConfirmationDialog();
        this.toastService.add(
          this.translatorService.translate(
            'tenantConfiguration.general.couldNotRemoveConfig'
          ),
          ToastType.ERROR
        );
        this.logger.error(error);
      },
    });
  }

  editConfig(event: {
    key: string;
    value: string;
    index: number;
    type: ConfigTypes;
  }): void {
    this.inputChanged.set(false);
    this.editMode.set(true);
    this.newConfigType.set(event.type);
    this.newConfigKey.set(event.key);
    this.hideOptionIndex.set(event.index);
    if (event.type === ConfigTypes.SECRET) {
      this.newConfigValue.set(null);
    } else {
      this.newConfigValue.set(event.value);
    }
  }

  cancelEdit(): void {
    this.editMode.set(false);
    this.newConfigKey.set(null);
    this.newConfigValue.set('');
    this.hideOptionIndex.set(null);
  }

  openConfirmationDialog(): void {
    this.removeConfirmOpen.set(true);
  }

  closeConfirmationDialog(): void {
    this.clearSelectedConfig();
    this.removeConfirmOpen.set(false);
  }

  clearSelectedConfig(): void {
    this.selectedOption.set('');
  }
}
